<template>
  <form-config-provider
    #default="{ formConfig: formConfigA }"
    :type="type"
    :fieldsMeta="fieldsMetaA"
  >
    <div class="history-page">
      <entity-modal-history v-if="hasHistory" v-model="version" :history="history" />

      <entity-view
        v-if="formConfigA"
        :type="type"
        :formConfig="formConfigA"
        :desktop="desktop"
        :hasHistory="hasHistory"
        showBackButton
        @goBack="goBack"
      >
        <template #form>
          <form-layout-provider
            #default="{ formLayout: formLayoutA }"
            :formConfig="formConfigA"
            :fieldsMeta="fieldsMetaA"
          >
            <a-spin :spinning="!formLayoutA || !entityDataA" class="edit-form-spinner">
              <entity-form
                v-if="formLayoutA && entityDataA"
                :id="id"
                :type="type"
                :desktop="desktop"
                :formLayout="formLayoutA"
                :sourceData="entityDataA"
                :refsData="entityRefsDataA"
                :readOnly="true"
                :oneColumn="true"
                :formConfig="formConfigA"
                :descriptors="fieldsMetaA"
                size="small"
                @editEntity="editEntity"
              >
                <template #bottomControls>
                  <a-button type="primary" @click="gotoEdit"> Редактировать </a-button>
                  <a-button v-if="!isLocalDraft && draftData" type="danger" @click="dropDraft">
                    Сбросить черновик
                  </a-button>
                </template>
              </entity-form>
            </a-spin>
          </form-layout-provider>
        </template>
        <template #compareForm>
          <form-config-provider
            #default="{ formConfig: formConfigB }"
            :type="type"
            :fieldsMeta="fieldsMetaB"
          >
            <form-layout-provider
              #default="{ formLayout: formLayoutB }"
              :formConfig="formConfigA"
              :fieldsMeta="fieldsMetaB"
            >
              <div>
                <div v-if="isLocalDraft" class="entity-view__no-form-msg">
                  <a-alert message="Данный объект ещё не сохранён в БД" />
                  <a-button type="primary" @click="dropDraft">
                    Удалить локальный черновик
                  </a-button>
                </div>
                <div v-else-if="hasHistory === false" class="entity-view__no-form-msg">
                  <a-alert message="История для данного объекта не найдена" />
                </div>
                <entity-form
                  v-else-if="formConfigB && entityDataB"
                  :id="id"
                  :type="type"
                  :desktop="desktop"
                  :formLayout="formLayoutB"
                  :sourceData="entityDataB"
                  :refsData="entityRefsDataB"
                  :readOnly="true"
                  :oneColumn="true"
                  :formConfig="formConfigB"
                  :descriptors="fieldsMetaB"
                  size="small"
                  @editEntity="editEntity"
                >
                  <template #bottomControls>
                    <a-button type="primary" @click="applyHistoryVersion(historyData.data)">
                      Применить
                    </a-button>
                  </template>
                </entity-form>
              </div>
            </form-layout-provider>
          </form-config-provider>
        </template>
      </entity-view>
    </div>
  </form-config-provider>
</template>

<script>
import store from '@/store';
import HISTORY_QUERY from '@/queries/history';
import HISTORY_ENTRY_QUERY from '@/queries/historyEntry';
import GET_QUERY from '@/queries/get';
import META_QUERY from '@/queries/meta';
import EntityForm from '@/components/edit-form/EntityForm.vue';
import EntityView from '@/components/edit-form/EntityView.vue';
import FormConfigProvider from '@/components/edit-form/FormConfigProvider.vue';
import FormLayoutProvider from '@/components/edit-form/FormLayoutProvider.vue';
import EntityModalHistory from './EntityModalHistory.vue';

export default {
  name: 'TheHistoryPage',
  components: {
    EntityModalHistory,
    FormConfigProvider,
    FormLayoutProvider,
    EntityForm,
    EntityView,
  },

  props: {
    desktop: {
      type: Boolean,
      required: true,
    },
    id: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: '',
    },
    extMeta: {
      type: Object,
      default: null,
    },
    isModal: {
      type: Boolean,
      default: false,
    },
  },

  apollo: {
    meta: {
      ...META_QUERY,
      variables() {
        return {
          type: this.type,
        };
      },
      skip() {
        return this.extMeta;
      },
      error(error) {
        this.emitError(`Ошибка получения meta-данных ${this.type}. ${error.message}`);
      },
    },
    history: {
      ...HISTORY_QUERY,
      fetchPolicy: 'network-only',
      variables() {
        return {
          entityName: this.type,
          entityId: this.id,
        };
      },
      update: ({ history }) => history && history.entries.slice().reverse(),
      error(error) {
        this.emitError(
          `Ошибка получения списка истории для ${this.type}:${this.id}. ${error.message}`,
        );
      },
    },
    sourceData: {
      ...GET_QUERY,
      variables() {
        return {
          id: this.id,
          type: this.type,
        };
      },
      skip() {
        return this.isLocalDraft;
      },
      error(error) {
        this.emitError(`Ошибка получения данных сущности ${this.id}:${this.id}. ${error.message}`);
      },
    },
    historyData: {
      ...HISTORY_ENTRY_QUERY,
      variables() {
        return {
          id: this.version,
        };
      },
      skip() {
        return !this.version;
      },
      error(error, vm, key, type, { variables }) {
        this.emitError(`Ошибка получения записи истории. Id: ${variables.id}. ${error.message};`);
      },
    },
  },

  data() {
    return {
      browserHistoryDepth: 1000,
      version: null,
      cachedConfig: null,
    };
  },

  computed: {
    title() {
      return this.cachedConfig
        ? this.cachedConfig.form.locale[store.state.lang].entity || this.type
        : '';
    },
    hasHistory() {
      return this.history && this.history.length > 0;
    },
    historyLoaded() {
      return !this.version || !this.$apollo.queries.historyData.loading;
    },
    formsMode() {
      return this.mode === 'history' ? 'common' : this.mode;
    },
    isLocalDraft() {
      return this.id.startsWith('_temp');
    },
    fieldsMetaA() {
      return this.meta?.components[this.type] ? this.meta?.components[this.type].fields : null;
    },
    draftData() {
      return store.mutate.getFormDraft(this.type, this.id)?.data;
    },
    entityDataA() {
      const data = this.isLocalDraft ? {} : this.sourceData?.document.data;
      return (
        data && {
          ...data,
          ...(this.draftData || {}),
        }
      );
    },
    entityRefsDataA() {
      return this.isLocalDraft ? {} : this.sourceData?.dictionary;
    },
    fieldsMetaB() {
      // @TODO брать из this.historyData.model когда с бека будет в том же формате что fields в meta
      return this.meta?.components[this.type] ? this.meta?.components[this.type].fields : null;
    },
    entityDataB() {
      return this.historyData?.data;
    },
    entityRefsDataB() {
      return this.historyData?.dictionary;
    },
  },

  beforeRouteEnter(to, from, next) {
    next((vm) => {
      if (from.name === null) vm.browserHistoryDepth = 0;
      else vm.browserHistoryDepth++;
    });
  },

  methods: {
    goBack() {
      if (this.browserHistoryDepth > 0) {
        this.browserHistoryDepth--;
        this.$router.go(-1);
      } else {
        this.gotoEdit();
      }
    },

    gotoEdit() {
      this.$router.push({
        name: 'TheEntityPage',
        params: {
          id: this.id,
          type: this.type,
        },
      });
    },

    applyHistoryVersion(data) {
      store.mutate.storeFormDraft(this.type, this.id, data);
      this.gotoEdit();
    },

    editEntity(...args) {
      this.browserHistoryDepth++;
      this.$emit('editEntity', ...args);
    },

    dropDraft() {
      store.mutate.deleteFormDraft(this.type, this.id);
      if (this.isLocalDraft) this.goBack();
    },
  },
};
</script>
